import {
    Component, Input, ViewChild, ElementRef,
    AfterViewInit, OnChanges, OnDestroy, OnInit,
    EventEmitter, Output
} from '@angular/core';
import { SeriesData } from '@app/shared/interfaces/chartSeries.interface';
import { echarts, ECharts, EChartsCoreOption } from '../echarts.config';
import type { BarSeriesOption } from 'echarts/charts';
import type { ECElementEvent } from 'echarts/core';

@Component({
    selector: 'app-chart-bar',
    templateUrl: './chart-bar.component.html',
    styleUrl: './chart-bar.component.scss',
    standalone: false
})
export class ChartBarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() categories: string[] = [];
    @Input() seriesData: SeriesData[] = [];

    @Input() title: string = '';
    @Input() subtitle: string = '';
    @Input() yAxisTitle: string = '';
    @Input() stackLabelsEnabled: boolean = true;

    @Output() pointClick = new EventEmitter<{ category: string; index: number }>();

    @ViewChild('container', { static: true }) container!: ElementRef;

    private chart: ECharts | null = null;
    private themeObserver!: MutationObserver;

    ngOnInit(): void {
        const html = document.documentElement;
        const body = document.body;

        if (html.classList.contains('app-dark')) {
            body.classList.add('app-dark');
        } else {
            body.classList.remove('app-dark');
        }

        this.themeObserver = new MutationObserver(() => {
            if (html.classList.contains('app-dark')) {
                body.classList.add('app-dark');
            } else {
                body.classList.remove('app-dark');
            }

            this.chart?.dispose();
            this.initChart();
        });

        this.themeObserver.observe(html, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngAfterViewInit(): void {
        this.initChart();
    }

    ngOnChanges(): void {
        if (this.chart) {
            this.updateChart();
        }
    }

    ngOnDestroy(): void {
        this.themeObserver?.disconnect();
        this.chart?.dispose();
    }

    private isDark(): boolean {
        return document.body.classList.contains('app-dark');
    }

    private theme() {
        const dark = this.isDark();
        return {
            text: dark ? '#e5e7eb' : '#374151',
            muted: dark ? '#9ca3af' : '#6b7280',
            tooltipBg: dark ? '#1f2937' : '#ffffff',
            tooltipBorder: dark ? '#374151' : '#e5e7eb',
            axisLineColor: dark ? '#555' : '#ccc'
        };
    }

    private initChart(): void {
        this.container.nativeElement.innerHTML = '';

        const theme = this.isDark() ? 'dark' : undefined;
        this.chart = echarts.init(this.container.nativeElement, theme);

        this.chart.setOption(this.buildOption());

        this.chart.on('click', (params: ECElementEvent) => {
            this.pointClick.emit({
                category: params.name,
                index: params.dataIndex
            });
        });

        setTimeout(() => this.chart?.resize(), 0);
    }

    private updateChart(): void {
        if (!this.chart) return;
        this.chart.setOption(this.buildOption(), { replaceMerge: ['series'] });
    }

    private buildOption(): EChartsCoreOption {
        const t = this.theme();

        return {
            backgroundColor: 'transparent',
            title: {
                text: this.title,
                subtext: this.subtitle,
                left: 'center',
                textStyle: { color: t.text }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                type: 'scroll',
                bottom: 0,
                textStyle: { color: t.text }
            },
            toolbox: {
                show: true,
                right: 16,
                top: 8,
                feature: {
                    saveAsImage: { name: 'grafico', title: 'Guardar' }
                },
                iconStyle: { borderColor: t.muted }
            },
            grid: {
                left: '3%',
                right: '8%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                name: this.yAxisTitle,
                nameLocation: 'middle',
                nameGap: 30,
                axisLine: { lineStyle: { color: t.axisLineColor } },
                axisLabel: { color: t.text },
                splitLine: { lineStyle: { color: t.axisLineColor, type: 'dashed' } }
            },
            yAxis: {
                type: 'category',
                data: this.categories,
                axisLine: { lineStyle: { color: t.axisLineColor } },
                axisLabel: { color: t.text }
            },
            series: this.getSeries()
        };
    }

    private getSeries(): BarSeriesOption[] {
        return this.seriesData.map((item) => ({
            name: item.name,
            type: 'bar',
            stack: 'total',
            data: item.values,
            ...(item.color ? { color: item.color } : {}),
            label: {
                show: true,
                position: 'inside',
                formatter: (params: any) => (params.value > 0 ? `${params.value}` : ''),
                fontSize: 13,
                color: '#fff'
            },
            emphasis: { focus: 'series' }
        }));
    }
}